import { z } from "zod";

/**
 * This validation runs at build time because Next.js inlines environment
 * variables during the build process. This lets us catch missing/invalid env
 * vars before deployment.
 *
 * See the README for info about each env var. Update it if you add a new one.
 */

const envSchema = z.object({
  NEXT_PUBLIC_API_BASE_URL: z.string().nonempty("API base URL is required"),
  NEXT_PUBLIC_VERSION: z.string().default("development_build"),
  NEXT_PUBLIC_ENVIRONMENT: z.enum(["test", "prod"], {
    errorMap: () => ({
      message: "Environment must be either 'test' or 'prod'",
    }),
  }),
  NEXT_PUBLIC_MICROSOFT_OAUTH_TENANT_ID: z.string().uuid(),
  NEXT_PUBLIC_CANVAS_BASE_URL: z
    .string()
    .nonempty("CANVAS base URL is required"),
});

const parseEnv = () => {
  try {
    return envSchema.parse({
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
      NEXT_PUBLIC_VERSION: process.env.NEXT_PUBLIC_VERSION,
      NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT,
      NEXT_PUBLIC_MICROSOFT_OAUTH_TENANT_ID:
        process.env.NEXT_PUBLIC_MICROSOFT_OAUTH_TENANT_ID,
      NEXT_PUBLIC_CANVAS_BASE_URL: process.env.NEXT_PUBLIC_CANVAS_BASE_URL,
    });
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessage = `❌ Invalid environment variables:\n${error.errors
        .map((err) => `${err.path}: ${err.message}`)
        .join("\n")}`;
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/** Validated environment variables. */
export const env: Readonly<ReturnType<typeof parseEnv>> = parseEnv();
